import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type MouseEvent,
} from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  addEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
  ReactFlowProvider,
  ConnectionMode,
  ConnectionLineType,
  type Connection,
  type Edge,
  type Node,
  type NodeProps,
  MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  ArrowLeft,
  Check,
  CheckSquare,
  ExternalLink,
  FileText,
  HelpCircle,
  Loader2,
  Lock,
  MessageSquare,
  MoreVertical,
  PanelRightClose,
  Plus,
  StickyNote,
  Trash2,
  Unlock,
  X,
} from "lucide-react";
import {
  api,
  type Mindmap,
  type MindmapChatMessage,
  type MindmapDiff,
  type MindmapDiffStatus,
  type MindmapGraph,
  type MindmapLink,
  type MindmapNode,
  type MindmapNodeClass,
  type MindmapNodeKind,
  type MindmapNodeStatus,
  type MindmapProposal,
  type Task,
  type DocDocument,
} from "@/lib/api";
import SaveOperatorInsightButton from "@/components/operator/SaveOperatorInsightButton";
import { MindmapEdge } from "@/components/mindmaps/MindmapEdge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const HISTORY_KEY = "mindmap-nav-stack";
const MAP_CHEAT_SHEET: { title: string; lines: string[] }[] = [
  {
    title: "Canvas",
    lines: [
      "Scroll: zoom",
      "Przeciągnij puste tło: przesuwanie mapy (pan)",
      "Na telefonie: cienka belka (← tytuł 🔒 ⋮); kłódka blokuje edycję (domyślnie włączona)",
      "Przeciągnij węzeł (kartę, nie kropkę): zmiana pozycji (autosave)",
      "Od małej kropki na środku przeciągnij do kropki drugiego węzła (A→B); linia omija inne karty",
      "Zaznaczony węzeł: + / − (albo przyciski w panelu) zmienia rozmiar, font i grubość połączeń",
      "W panelu: kolor karty (paleta)",
      "Klik węzeł: panel edycji na dole",
      "Klik tła: odznacz węzeł",
      "Delete / Backspace: usuń zaznaczony węzeł (gdy focus nie jest w polu tekstowym)",
      "Dwuklik portal: otwórz mapę docelową",
    ],
  },
  {
    title: "Przyciski i panel",
    lines: [
      "Węzeł / Karteczka / Portal: dodaj element",
      "W panelu węzła: tytuł, opis, kind, class, status, tagi, checklist, linki",
      "Utwórz zadanie w Kanbanie: nowe zadanie z treści węzła + link zwrotny do mapy",
      "Utwórz dokument: nowy dokument w Dokumentach z tytułu/opisu węzła",
      "Link do mapy: podłącz inną mapę myśli (nawigacja z historii wstecz)",
      "Filtr class / tag: ukrywa niedopasowane węzły",
    ],
  },
  {
    title: "Chat i propozycje",
    lines: [
      "Napisz np. „Zbuduj mapę z tego: …” / „Połącz A z B” / „Przepnij krawędź”",
      "Chat pokazuje podgląd (zielony = nowy, żółty = zmiana, czerwony = usuń; krawędzie też)",
      "Zatwierdź zapisuje graf; Odrzuć wraca do zapisanego stanu",
      "W trybie podglądu: edycja canvasa i autosave są wstrzymane",
      "Zaznaczony węzeł idzie do chatu jako focus (łatwiej o połączenia)",
    ],
  },
];
const CLASSES: MindmapNodeClass[] = [
  "strategy",
  "customer",
  "offer",
  "content",
  "channel",
  "ops",
  "learning",
  "hypothesis",
  "decision",
  "meta",
];
const STATUSES: MindmapNodeStatus[] = [
  "hipoteza",
  "opcja",
  "decyzja",
  "fakt",
  "w_toku",
  "done",
];
const KINDS: MindmapNodeKind[] = [
  "idea",
  "system",
  "doc",
  "task",
  "checklist",
  "portal",
  "note",
  "sticky",
];
const STICKY_COLORS = ["white", "yellow", "pink", "blue", "green", "violet", "orange", "slate"] as const;
const NODE_SCALE_MIN = 1;
const NODE_SCALE_MAX = 5;
const NODE_SCALE_DEFAULT = 3;

function useIsMobileMd(): boolean {
  const [mobile, setMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return mobile;
}

type RfData = MindmapNode & { selectedFocus?: boolean; diffStatus?: MindmapDiffStatus };

function clampScale(raw?: number): number {
  const n = Number.isFinite(raw) ? Math.round(raw as number) : NODE_SCALE_DEFAULT;
  return Math.min(NODE_SCALE_MAX, Math.max(NODE_SCALE_MIN, n));
}

function scaleTokens(scale?: number) {
  const s = clampScale(scale);
  return {
    s,
    titlePx: [11, 12, 14, 16, 18][s - 1],
    bodyPx: [9, 10, 11, 12, 13][s - 1],
    badgePx: [9, 9, 10, 10, 11][s - 1],
    minW: [110, 125, 140, 170, 200][s - 1],
    maxW: [160, 190, 220, 280, 340][s - 1],
    padX: [8, 9, 10, 12, 14][s - 1],
    padY: [6, 7, 8, 10, 12][s - 1],
  };
}

function nodeBg(color?: string) {
  switch (color) {
    case "pink":
      return "bg-pink-100 border-pink-300";
    case "blue":
      return "bg-sky-100 border-sky-300";
    case "green":
      return "bg-emerald-100 border-emerald-300";
    case "violet":
      return "bg-violet-100 border-violet-300";
    case "orange":
      return "bg-orange-100 border-orange-300";
    case "slate":
      return "bg-slate-100 border-slate-300";
    case "yellow":
      return "bg-amber-100 border-amber-300";
    case "white":
    default:
      return "bg-white border-slate-200";
  }
}

function colorSwatch(color: string) {
  switch (color) {
    case "pink":
      return "bg-pink-300";
    case "blue":
      return "bg-sky-300";
    case "green":
      return "bg-emerald-300";
    case "violet":
      return "bg-violet-300";
    case "orange":
      return "bg-orange-300";
    case "slate":
      return "bg-slate-400";
    case "yellow":
      return "bg-amber-300";
    default:
      return "bg-white border border-slate-300";
  }
}

function MindNode({ data, selected }: NodeProps) {
  const d = data as unknown as RfData;
  const isPortal = d.kind === "portal";
  const diff = d.diffStatus || d.diff_status;
  const tok = scaleTokens(d.scale);
  return (
    <div
      className={cn(
        "relative rounded-lg border text-left shadow-sm",
        nodeBg(d.color),
        selected && !diff && "ring-2 ring-indigo-400",
        isPortal && "border-dashed border-indigo-400",
        diff === "add" && "ring-2 ring-emerald-500 border-emerald-400",
        diff === "update" && "ring-2 ring-amber-500 border-amber-400",
        diff === "delete" && "border-dashed border-red-500 opacity-60 ring-2 ring-red-400",
      )}
      style={{
        minWidth: tok.minW,
        maxWidth: tok.maxW,
        padding: `${tok.padY}px ${tok.padX}px`,
      }}
      title={d.diff_old_title ? `Było: ${d.diff_old_title}` : undefined}
    >
      {diff ? (
        <span
          className={cn(
            "absolute -right-1 -top-2 rounded px-1 text-[9px] font-semibold uppercase text-white",
            diff === "add" && "bg-emerald-600",
            diff === "update" && "bg-amber-600",
            diff === "delete" && "bg-red-600",
          )}
        >
          {diff === "add" ? "nowy" : diff === "update" ? "zmiana" : "usuń"}
        </span>
      ) : null}
      <Handle
        type="target"
        position={Position.Top}
        id="t"
        className="!pointer-events-auto"
        style={{
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: 18,
          height: 18,
          border: "none",
          background: "transparent",
          opacity: 0,
          zIndex: 15,
        }}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="s"
        title="Przeciągnij, żeby połączyć"
        style={{
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: 12,
          height: 12,
          border: "2px solid white",
          background: "#64748b",
          zIndex: 20,
        }}
      />
      <div className="mb-1 flex flex-wrap gap-1">
        <Badge variant="secondary" style={{ fontSize: tok.badgePx }} className="leading-none">
          {d.class}
        </Badge>
        {d.kind !== "idea" ? (
          <Badge variant="outline" style={{ fontSize: tok.badgePx }} className="leading-none">
            {d.kind}
          </Badge>
        ) : null}
      </div>
      <div
        className="font-medium leading-snug text-slate-900"
        style={{ fontSize: tok.titlePx }}
      >
        {d.title}
      </div>
      {d.body ? (
        <p
          className="mt-1 line-clamp-3 leading-snug text-slate-600"
          style={{ fontSize: tok.bodyPx }}
        >
          {d.body}
        </p>
      ) : null}
      {(d.tags || []).length > 0 ? (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {(d.tags || []).slice(0, 3).map((t) => (
            <span
              key={t}
              className="rounded bg-black/5 px-1 text-slate-600"
              style={{ fontSize: tok.badgePx }}
            >
              #{t}
            </span>
          ))}
        </div>
      ) : null}
      <div className="mt-1 text-slate-400" style={{ fontSize: tok.badgePx }}>
        {d.status}
      </div>
    </div>
  );
}

const nodeTypes = { mind: MindNode };
const edgeTypes = { mindmap: MindmapEdge };

function edgeStyleForDiff(status?: MindmapDiffStatus): Partial<Edge> {
  if (status === "add") {
    return { style: { stroke: "#059669", strokeWidth: 2 }, animated: true };
  }
  if (status === "update") {
    return { style: { stroke: "#d97706", strokeWidth: 2 } };
  }
  if (status === "delete") {
    return {
      style: { stroke: "#dc2626", strokeWidth: 2, strokeDasharray: "6 4" },
      animated: false,
    };
  }
  return {};
}

function graphToFlow(
  graph: MindmapGraph,
  diff?: MindmapDiff | null,
): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = (graph.nodes || []).map((n) => {
    const status = n.diff_status || diff?.nodes?.[n.id];
    return toFlowNode({
      ...n,
      diff_status: status,
      diff_old_title: n.diff_old_title || diff?.old_titles?.[n.id],
    });
  });
  const edges: Edge[] = (graph.edges || []).map((e) => {
    const status = e.diff_status || diff?.edges?.[e.id];
    return {
      id: e.id,
      type: "mindmap",
      source: e.source,
      target: e.target,
      label: e.label || e.kind,
      markerEnd: { type: MarkerType.ArrowClosed },
      data: { kind: e.kind, diffStatus: status },
      ...edgeStyleForDiff(status),
    };
  });
  return { nodes, edges };
}

function storeToFlow(map: Mindmap, diff?: MindmapDiff | null): { nodes: Node[]; edges: Edge[] } {
  return graphToFlow(map.graph, diff);
}

function FitChangedNodes({ nodeIds }: { nodeIds: string[] }) {
  const { fitView } = useReactFlow();
  useEffect(() => {
    if (!nodeIds.length) return;
    const t = window.setTimeout(() => {
      void fitView({
        nodes: nodeIds.map((id) => ({ id })),
        padding: 0.35,
        duration: 400,
      });
    }, 80);
    return () => window.clearTimeout(t);
  }, [nodeIds.join(","), fitView]);
  return null;
}

function flowToGraph(nodes: Node[], edges: Edge[]): Mindmap["graph"] {
  return {
    nodes: nodes.map((n) => {
      const d = n.data as unknown as MindmapNode;
      return {
        ...d,
        id: n.id,
        position: { x: n.position.x, y: n.position.y },
      };
    }),
    edges: edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      kind:
        ((e.data as { kind?: string } | undefined)?.kind as Mindmap["graph"]["edges"][0]["kind"]) ||
        "relates",
      label: typeof e.label === "string" ? e.label : "",
    })),
  };
}

function pushHistory(fromId: string) {
  try {
    const raw = sessionStorage.getItem(HISTORY_KEY);
    const stack: string[] = raw ? (JSON.parse(raw) as string[]) : [];
    if (stack[stack.length - 1] !== fromId) stack.push(fromId);
    sessionStorage.setItem(HISTORY_KEY, JSON.stringify(stack.slice(-20)));
  } catch {
    /* ignore */
  }
}

function popHistory(): string | null {
  try {
    const raw = sessionStorage.getItem(HISTORY_KEY);
    const stack: string[] = raw ? (JSON.parse(raw) as string[]) : [];
    const prev = stack.pop() || null;
    sessionStorage.setItem(HISTORY_KEY, JSON.stringify(stack));
    return prev;
  } catch {
    return null;
  }
}

function newNode(partial: Partial<MindmapNode>): MindmapNode {
  const id = `n-${crypto.randomUUID().slice(0, 8)}`;
  return {
    kind: partial.kind || "idea",
    class: partial.class || "hypothesis",
    status: partial.status || "hipoteza",
    title: partial.title || "Nowy węzeł",
    body: partial.body || "",
    tags: partial.tags || [],
    links: partial.links || [],
    checkboxes: partial.checkboxes || [],
    position: partial.position || { x: 80, y: 80 },
    color: partial.color || (partial.kind === "sticky" ? "yellow" : "white"),
    scale: clampScale(partial.scale ?? NODE_SCALE_DEFAULT),
    checked: false,
    ...partial,
    id,
  };
}

function toFlowNode(n: MindmapNode): Node {
  return {
    id: n.id,
    type: "mind",
    position: n.position || { x: 0, y: 0 },
    data: {
      ...n,
      diffStatus: n.diff_status,
    } as unknown as Record<string, unknown>,
  };
}

export default function MapaDetail() {
  return (
    <ReactFlowProvider>
      <MapaDetailInner />
    </ReactFlowProvider>
  );
}

function MapaDetailInner() {
  const { id = "" } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { setCenter, getNode } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const isMobile = useIsMobileMd();
  const [chatOpen, setChatOpen] = useState(() =>
    typeof window !== "undefined" ? !window.matchMedia("(max-width: 767px)").matches : true,
  );
  const [chatInput, setChatInput] = useState("");
  const [filterClass, setFilterClass] = useState<string>("");
  const [filterTag, setFilterTag] = useState("");
  const [previewTask, setPreviewTask] = useState<Task | null>(null);
  const [previewDoc, setPreviewDoc] = useState<(DocDocument & { truncated?: boolean }) | null>(
    null,
  );
  const [mapsIndex, setMapsIndex] = useState<{ id: string; title: string }[]>([]);
  const [proposal, setProposal] = useState<MindmapProposal | null>(null);
  const [titleDraft, setTitleDraft] = useState("");
  const [descDraft, setDescDraft] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileEditLocked, setMobileEditLocked] = useState(true);
  const saveTimer = useRef<number | null>(null);
  const hydrated = useRef(false);
  const skipSave = useRef(true);
  const proposalActive = Boolean(proposal);
  const canvasLocked = proposalActive || (isMobile && mobileEditLocked);

  useEffect(() => {
    setChatOpen(!isMobile);
    if (isMobile) setMobileEditLocked(true);
  }, [isMobile]);

  const mapQ = useQuery({
    queryKey: ["mindmap", id],
    queryFn: () => api.mindmapsGet(id),
    enabled: Boolean(id),
  });

  const chatQ = useQuery({
    queryKey: ["mindmap-chat", id],
    queryFn: () => api.mindmapsChatList(id),
    enabled: Boolean(id),
  });

  useEffect(() => {
    void api.mindmapsList().then((r) =>
      setMapsIndex(r.items.map((m) => ({ id: m.id, title: m.title }))),
    );
  }, []);

  useEffect(() => {
    if (!mapQ.data?.map) return;
    setTitleDraft(mapQ.data.map.title || "");
    setDescDraft(mapQ.data.map.description || "");
  }, [mapQ.data?.map?.id, mapQ.data?.map?.title, mapQ.data?.map?.description]);

  useEffect(() => {
    hydrated.current = false;
    skipSave.current = true;
    if (!mapQ.data?.map) return;
    const mapData = mapQ.data.map;
    const prop =
      mapData.proposal ||
      (mapQ.data as { proposal?: MindmapProposal | null }).proposal ||
      null;
    setProposal(prop && prop.preview_graph ? prop : null);
    if (prop?.preview_graph) {
      const { nodes: n, edges: e } = graphToFlow(prop.preview_graph, prop.diff);
      setNodes(n);
      setEdges(e);
    } else {
      const { nodes: n, edges: e } = storeToFlow(mapData);
      setNodes(n);
      setEdges(e);
    }
    hydrated.current = true;
    const t = window.setTimeout(() => {
      skipSave.current = false;
    }, 50);
    return () => window.clearTimeout(t);
  }, [mapQ.data?.map?.id, mapQ.data?.map?.updated_at, setNodes, setEdges]);

  const saveGraph = useCallback(
    (nextNodes: Node[], nextEdges: Edge[]) => {
      if (!id || !hydrated.current || skipSave.current || proposalActive) return;
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(() => {
        const graph = flowToGraph(nextNodes, nextEdges);
        void api.mindmapsPutGraph(id, { graph }).then((res) => {
          void qc.setQueryData(["mindmap", id], (old: { map: Mindmap } | undefined) => {
            if (!old?.map) return res;
            return {
              map: {
                ...old.map,
                progress: res.map.progress,
                updated_at: res.map.updated_at,
                graph: res.map.graph,
              },
              proposal: old.map.proposal ?? null,
            };
          });
        });
      }, 600);
    },
    [id, qc, proposalActive],
  );

  const onNodesChangeWrapped = useCallback(
    (changes: Parameters<typeof onNodesChange>[0]) => {
      if (canvasLocked) return;
      onNodesChange(changes);
    },
    [onNodesChange, canvasLocked],
  );

  const onEdgesChangeWrapped = useCallback(
    (changes: Parameters<typeof onEdgesChange>[0]) => {
      if (canvasLocked) return;
      onEdgesChange(changes);
    },
    [onEdgesChange, canvasLocked],
  );

  useEffect(() => {
    if (!hydrated.current || !id || skipSave.current || proposalActive || canvasLocked) return;
    saveGraph(nodes, edges);
  }, [nodes, edges, id, saveGraph, proposalActive, canvasLocked]);

  const onConnect = useCallback(
    (c: Connection) => {
      if (canvasLocked) return;
      setEdges((eds) =>
        addEdge(
          {
            ...c,
            id: `e-${c.source}-${c.target}-${crypto.randomUUID().slice(0, 4)}`,
            type: "mindmap",
            markerEnd: { type: MarkerType.ArrowClosed },
            data: { kind: "relates" },
            label: "relates",
          },
          eds,
        ),
      );
    },
    [setEdges, canvasLocked],
  );

  const selected = useMemo(() => {
    if (!selectedId) return null;
    const n = nodes.find((x) => x.id === selectedId);
    return n ? (n.data as unknown as MindmapNode) : null;
  }, [nodes, selectedId]);

  const visibleNodes = useMemo(() => {
    return nodes.map((n) => {
      const d = n.data as unknown as MindmapNode;
      const hideClass = filterClass && d.class !== filterClass;
      const hideTag = filterTag && !(d.tags || []).includes(filterTag.trim().toLowerCase());
      return { ...n, hidden: Boolean(hideClass || hideTag) };
    });
  }, [nodes, filterClass, filterTag]);

  const patchSelected = (patch: Partial<MindmapNode>) => {
    if (!selectedId || canvasLocked) return;
    setNodes((curr) =>
      curr.map((n) =>
        n.id === selectedId
          ? {
              ...n,
              data: {
                ...(n.data as unknown as MindmapNode),
                ...patch,
                ...(patch.scale !== undefined ? { scale: clampScale(patch.scale) } : {}),
              } as unknown as Record<string, unknown>,
            }
          : n,
      ),
    );
  };

  const bumpSelectedScale = useCallback(
    (delta: number) => {
      if (!selectedId || canvasLocked) return;
      setNodes((curr) =>
        curr.map((n) => {
          if (n.id !== selectedId) return n;
          const d = n.data as unknown as MindmapNode;
          return {
            ...n,
            data: {
              ...d,
              scale: clampScale((d.scale ?? NODE_SCALE_DEFAULT) + delta),
            } as unknown as Record<string, unknown>,
          };
        }),
      );
    },
    [selectedId, canvasLocked, setNodes],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (canvasLocked || !selectedId) return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || t?.isContentEditable) {
        return;
      }
      if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        bumpSelectedScale(1);
      } else if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        bumpSelectedScale(-1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId, canvasLocked, bumpSelectedScale]);

  const addNode = (kind: MindmapNodeKind = "idea") => {
    if (canvasLocked) return;
    const n = newNode({
      kind,
      class: kind === "sticky" ? "meta" : kind === "portal" ? "meta" : "hypothesis",
      title: kind === "sticky" ? "Karteczka" : kind === "portal" ? "→ Mapa" : "Nowy węzeł",
      position: { x: 120 + Math.random() * 80, y: 100 + Math.random() * 80 },
    });
    setNodes((curr) => [...curr, toFlowNode(n)]);
    setSelectedId(n.id);
  };

  const removeSelected = () => {
    if (!selectedId || canvasLocked) return;
    setNodes((curr) => curr.filter((n) => n.id !== selectedId));
    setEdges((eds) => eds.filter((e) => e.source !== selectedId && e.target !== selectedId));
    setSelectedId(null);
  };

  const applyProposalToCanvas = (prop: MindmapProposal | null, mapData: Mindmap) => {
    skipSave.current = true;
    setProposal(prop);
    if (prop?.preview_graph) {
      const { nodes: n, edges: e } = graphToFlow(prop.preview_graph, prop.diff);
      setNodes(n);
      setEdges(e);
    } else {
      const { nodes: n, edges: e } = storeToFlow(mapData);
      setNodes(n);
      setEdges(e);
    }
    window.setTimeout(() => {
      skipSave.current = false;
    }, 50);
  };

  const chatM = useMutation({
    mutationFn: (message: string) =>
      api.mindmapsChat(id, {
        message,
        focus_ids: selectedId ? [selectedId] : [],
      }),
    onSuccess: (res) => {
      void qc.setQueryData(["mindmap-chat", id], { messages: res.messages });
      setChatInput("");
      if (res.proposal?.preview_graph) {
        const nextMap = res.map || mapQ.data?.map;
        if (nextMap) {
          void qc.setQueryData(["mindmap", id], {
            map: { ...nextMap, proposal: res.proposal },
            proposal: res.proposal,
          });
          applyProposalToCanvas(res.proposal, nextMap);
        }
      } else if (res.map) {
        void qc.setQueryData(["mindmap", id], { map: res.map, proposal: null });
      }
    },
  });

  const acceptM = useMutation({
    mutationFn: () => api.mindmapsProposalAccept(id),
    onSuccess: (res) => {
      void qc.setQueryData(["mindmap", id], { map: res.map, proposal: null });
      applyProposalToCanvas(null, res.map);
    },
  });

  const rejectM = useMutation({
    mutationFn: () => api.mindmapsProposalReject(id),
    onSuccess: (res) => {
      void qc.setQueryData(["mindmap", id], { map: res.map, proposal: null });
      applyProposalToCanvas(null, res.map);
    },
  });

  const metaM = useMutation({
    mutationFn: (body: { title: string; description: string }) => api.mindmapsPatch(id, body),
    onSuccess: (res) => {
      void qc.setQueryData(["mindmap", id], {
        map: { ...res.map, proposal: proposal ?? res.map.proposal ?? null },
        proposal: proposal ?? res.proposal ?? null,
      });
      void qc.invalidateQueries({ queryKey: ["mindmaps"] });
      setTitleDraft(res.map.title || "");
      setDescDraft(res.map.description || "");
    },
  });

  const saveMapMeta = () => {
    const current = mapQ.data?.map;
    if (!current || proposalActive || metaM.isPending) return;
    const title = titleDraft.trim() || "Bez tytułu";
    const description = descDraft.trim();
    if (title === current.title && description === (current.description || "")) return;
    metaM.mutate({ title, description });
  };

  const focusChangedNode = (nodeId: string) => {
    setSelectedId(nodeId);
    const n = getNode(nodeId);
    if (n) {
      void setCenter(n.position.x + 80, n.position.y + 40, { zoom: 1.1, duration: 350 });
    }
  };

  const promoteM = useMutation({
    mutationFn: () =>
      api.mindmapsPromoteTask(id, {
        node_id: selectedId!,
        owner: "arek",
        panel_origin: window.location.origin,
      }),
    onSuccess: (res) => {
      void qc.setQueryData(["mindmap", id], res);
      const { nodes: n, edges: e } = storeToFlow(res.map);
      setNodes(n);
      setEdges(e);
    },
  });

  const linkDocM = useMutation({
    mutationFn: () => api.mindmapsLinkDoc(id, { node_id: selectedId! }),
    onSuccess: (res) => {
      void qc.setQueryData(["mindmap", id], res);
      const { nodes: n, edges: e } = storeToFlow(res.map);
      setNodes(n);
      setEdges(e);
    },
  });

  const openLink = async (link: MindmapLink) => {
    if (link.type === "task") {
      const res = await api.mindmapsPreviewTask(id, link.ref);
      setPreviewTask(res.task);
      return;
    }
    if (link.type === "doc") {
      const res = await api.mindmapsPreviewDoc(id, link.ref);
      setPreviewDoc(res.document);
      return;
    }
    if (link.type === "map") {
      const target = link.ref.trim();
      if (!target || target === id) return;
      pushHistory(id);
      navigate(`/mapy/${target}?from=${encodeURIComponent(id)}`);
      return;
    }
    if (link.type === "route") {
      navigate(link.ref.startsWith("/") ? link.ref : `/${link.ref}`);
      return;
    }
    if (link.type === "wiedza") {
      navigate(`/wiedza?q=${encodeURIComponent(link.ref)}`);
      return;
    }
    if (link.type === "ssot") {
      navigate(`/wiedza`);
      return;
    }
    if (link.type === "external") {
      window.open(link.ref, "_blank", "noopener,noreferrer");
    }
  };

  const addMapLink = (mapId: string) => {
    if (!selected || !mapId || mapId === id) return;
    const already = (selected.links || []).some((l) => l.type === "map" && l.ref === mapId);
    if (already) return;
    const title = mapsIndex.find((m) => m.id === mapId)?.title || "Mapa";
    patchSelected({
      links: [...(selected.links || []), { type: "map", ref: mapId, label: title }],
    });
  };

  const onNodeDoubleClick = (_: MouseEvent, node: Node) => {
    const d = node.data as unknown as MindmapNode;
    if (d.kind === "portal" && d.target_map_id) {
      pushHistory(id);
      navigate(`/mapy/${d.target_map_id}?from=${encodeURIComponent(id)}`);
    }
  };

  const goBack = () => {
    const prev = popHistory() || searchParams.get("from");
    if (prev) navigate(`/mapy/${prev}`);
    else navigate("/mapy");
  };

  const messages: MindmapChatMessage[] = chatQ.data?.messages || [];
  const map = mapQ.data?.map;
  const diff = proposal?.diff;
  const changedNodeIds = diff?.changed_node_ids || [];

  if (mapQ.isLoading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Ładowanie mapy…
      </div>
    );
  }
  if (!map) {
    return (
      <div className="p-6">
        <p className="text-sm text-slate-600">Nie znaleziono mapy.</p>
        <Link to="/mapy" className="text-sm text-indigo-600">
          Wróć do listy
        </Link>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col",
        isMobile
          ? "h-[calc(100dvh-3.75rem)] gap-0"
          : "h-[calc(100vh-5.5rem)] gap-3",
      )}
    >
      {isMobile ? (
        <div className="relative z-20 flex shrink-0 items-center gap-1 border-b border-slate-200/80 bg-white px-0.5 py-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-9 w-9 shrink-0 p-0"
            onClick={goBack}
            aria-label="Wróć"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0 flex-1 truncate px-1 text-sm font-semibold text-slate-900">
            {titleDraft || "Mapa"}
          </div>
          <Button
            variant={mobileEditLocked ? "secondary" : "ghost"}
            size="sm"
            className={cn(
              "h-9 w-9 shrink-0 p-0",
              !mobileEditLocked && "text-amber-700",
            )}
            onClick={() => setMobileEditLocked((v) => !v)}
            aria-label={mobileEditLocked ? "Odblokuj edycję" : "Zablokuj edycję"}
            title={
              mobileEditLocked
                ? "Podgląd: edycja zablokowana (kliknij, by edytować)"
                : "Edycja włączona (kliknij, by zablokować)"
            }
          >
            {mobileEditLocked ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
          </Button>
          <Button
            variant={mobileMenuOpen ? "secondary" : "ghost"}
            size="sm"
            className="h-9 w-9 shrink-0 p-0"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label="Opcje mapy"
            title="Opcje"
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
          {mobileMenuOpen ? (
            <>
              <button
                type="button"
                className="fixed inset-0 z-30 bg-black/30"
                aria-label="Zamknij opcje"
                onClick={() => setMobileMenuOpen(false)}
              />
              <div className="absolute right-1 top-full z-40 mt-1 w-[min(100vw-1rem,320px)] rounded-xl border bg-white p-3 shadow-xl">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Opcje
                  </span>
                  <Badge variant="secondary" className="text-[10px]">
                    Postęp {Math.round(map.progress)}%
                  </Badge>
                </div>
                <Input
                  className="mb-2 h-8 text-sm"
                  value={titleDraft}
                  disabled={canvasLocked || metaM.isPending}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={saveMapMeta}
                  aria-label="Nazwa mapy"
                  placeholder="Nazwa mapy"
                />
                <Input
                  className="mb-3 h-8 text-sm"
                  value={descDraft}
                  disabled={canvasLocked || metaM.isPending}
                  onChange={(e) => setDescDraft(e.target.value)}
                  onBlur={saveMapMeta}
                  aria-label="Opis mapy"
                  placeholder="Opis (opcjonalnie)"
                />
                <div className="mb-3 grid grid-cols-2 gap-2">
                  <select
                    className="rounded border px-2 py-1.5 text-xs"
                    value={filterClass}
                    onChange={(e) => setFilterClass(e.target.value)}
                  >
                    <option value="">Wszystkie class</option>
                    {CLASSES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <Input
                    className="h-8 text-xs"
                    placeholder="Filtr tag"
                    value={filterTag}
                    onChange={(e) => setFilterTag(e.target.value)}
                  />
                </div>
                {mobileEditLocked ? (
                  <p className="mb-2 rounded-md bg-slate-50 px-2 py-1.5 text-[11px] text-slate-600">
                    Edycja zablokowana. Odblokuj kłódką w belce, żeby przesuwać węzły i dodawać elementy.
                  </p>
                ) : null}
                <div className="flex flex-col gap-1.5">
                  <Button
                    size="sm"
                    variant="outline"
                    className="justify-start"
                    disabled={canvasLocked}
                    onClick={() => {
                      addNode("idea");
                      setMobileMenuOpen(false);
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" /> Węzeł
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="justify-start"
                    disabled={canvasLocked}
                    onClick={() => {
                      addNode("sticky");
                      setMobileMenuOpen(false);
                    }}
                  >
                    <StickyNote className="mr-2 h-4 w-4" /> Karteczka
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="justify-start"
                    disabled={canvasLocked}
                    onClick={() => {
                      addNode("portal");
                      setMobileMenuOpen(false);
                    }}
                  >
                    Portal
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="justify-start"
                    onClick={() => {
                      setHelpOpen(true);
                      setMobileMenuOpen(false);
                    }}
                  >
                    <HelpCircle className="mr-2 h-4 w-4" /> Skróty
                  </Button>
                  <Button
                    size="sm"
                    className="justify-start"
                    onClick={() => {
                      setChatOpen(true);
                      setMobileMenuOpen(false);
                    }}
                  >
                    <MessageSquare className="mr-2 h-4 w-4" /> Chat
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-wrap items-start gap-2">
          <Button variant="outline" size="sm" className="mt-1 shrink-0" onClick={goBack}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Wróć
          </Button>
          <div className="min-w-0 flex-1 space-y-1">
            <Input
              className="h-9 border-transparent bg-transparent px-1 text-xl font-semibold shadow-none focus-visible:border-slate-200 focus-visible:bg-white"
              value={titleDraft}
              disabled={proposalActive || metaM.isPending}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={saveMapMeta}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  (e.target as HTMLInputElement).blur();
                }
              }}
              aria-label="Nazwa mapy"
              placeholder="Nazwa mapy"
            />
            <Input
              className="h-8 border-transparent bg-transparent px-1 text-sm text-slate-600 shadow-none focus-visible:border-slate-200 focus-visible:bg-white"
              value={descDraft}
              disabled={proposalActive || metaM.isPending}
              onChange={(e) => setDescDraft(e.target.value)}
              onBlur={saveMapMeta}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  (e.target as HTMLInputElement).blur();
                }
              }}
              aria-label="Opis mapy"
              placeholder="Opis mapy (opcjonalnie)"
            />
          </div>
          <Badge variant="secondary" className="mt-2 shrink-0">
            Postęp {Math.round(map.progress)}%
          </Badge>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={helpOpen ? "default" : "outline"}
              onClick={() => setHelpOpen((v) => !v)}
              title="Jak korzystać z mapy"
            >
              <HelpCircle className="mr-1 h-4 w-4" /> Skróty
            </Button>
            <Button size="sm" variant="outline" disabled={proposalActive} onClick={() => addNode("idea")}>
              <Plus className="mr-1 h-4 w-4" /> Węzeł
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={proposalActive}
              onClick={() => addNode("sticky")}
            >
              <StickyNote className="mr-1 h-4 w-4" /> Karteczka
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={proposalActive}
              onClick={() => addNode("portal")}
            >
              Portal
            </Button>
            <Button
              size="sm"
              variant={chatOpen ? "default" : "outline"}
              onClick={() => setChatOpen((v) => !v)}
              title={chatOpen ? "Ukryj chat" : "Pokaż chat"}
            >
              {chatOpen ? (
                <PanelRightClose className="mr-1 h-4 w-4" />
              ) : (
                <MessageSquare className="mr-1 h-4 w-4" />
              )}
              Chat
            </Button>
          </div>
        </div>
      )}

      {helpOpen ? (
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 shadow-sm sm:mt-0">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="font-medium text-slate-900">Cheat sheet: jak pracować z mapą</span>
            <button type="button" className="text-slate-400 hover:text-slate-700" onClick={() => setHelpOpen(false)}>
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {MAP_CHEAT_SHEET.map((sec) => (
              <div key={sec.title}>
                <div className="mb-1 font-semibold uppercase tracking-wide text-[10px] text-slate-500">
                  {sec.title}
                </div>
                <ul className="list-disc space-y-1 pl-4 leading-snug">
                  {sec.lines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {proposal && diff ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950">
          <span className="font-medium">Propozycja chatu:</span>
          <span className="text-xs">{diff.summary || "zmiany na mapie"}</span>
          <Badge variant="secondary" className="text-[10px]">
            +{diff.counts.add} / ~{diff.counts.update} / −{diff.counts.delete}
          </Badge>
          <div className="ml-auto flex gap-2">
            <Button
              size="sm"
              disabled={acceptM.isPending || rejectM.isPending}
              onClick={() => acceptM.mutate()}
            >
              {acceptM.isPending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="mr-1 h-3.5 w-3.5" />
              )}
              Zatwierdź
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={acceptM.isPending || rejectM.isPending}
              onClick={() => rejectM.mutate()}
            >
              <X className="mr-1 h-3.5 w-3.5" /> Odrzuć
            </Button>
          </div>
        </div>
      ) : null}

      <div className="hidden flex-wrap gap-2 text-xs sm:flex">
        <select
          className="rounded border px-2 py-1"
          value={filterClass}
          onChange={(e) => setFilterClass(e.target.value)}
        >
          <option value="">Wszystkie class</option>
          {CLASSES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <Input
          className="h-8 max-w-[160px]"
          placeholder="Filtr tag"
          value={filterTag}
          onChange={(e) => setFilterTag(e.target.value)}
        />
        {proposalActive ? (
          <span className="self-center text-amber-700">
            Podgląd propozycji: edycja i autosave wstrzymane
          </span>
        ) : null}
      </div>

      <div
        className={cn(
          "flex min-h-0 flex-1 gap-3",
          isMobile && "-mx-3 min-h-0 flex-1 border-0 sm:mx-0",
        )}
      >
        <div
          className={cn(
            "relative min-w-0 flex-1 overflow-hidden bg-slate-50",
            isMobile ? "rounded-none border-0" : "rounded-xl border",
          )}
        >
          <ReactFlow
            nodes={visibleNodes}
            edges={edges}
            onNodesChange={onNodesChangeWrapped}
            onEdgesChange={onEdgesChangeWrapped}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            connectionMode={ConnectionMode.Loose}
            connectionLineType={ConnectionLineType.Straight}
            connectOnClick={false}
            defaultEdgeOptions={{
              type: "mindmap",
              markerEnd: { type: MarkerType.ArrowClosed },
            }}
            isValidConnection={(c) => Boolean(c.source && c.target && c.source !== c.target)}
            onNodeClick={(_, n) => setSelectedId(n.id)}
            onNodeDoubleClick={onNodeDoubleClick}
            onPaneClick={() => setSelectedId(null)}
            nodesDraggable={!canvasLocked}
            nodesConnectable={!canvasLocked}
            elementsSelectable
            selectNodesOnDrag={false}
            minZoom={0.12}
            maxZoom={2.5}
            zoomOnPinch
            panOnDrag
            fitView
            proOptions={{ hideAttribution: true }}
          >
            <Background />
            {!isMobile ? <Controls /> : null}
            {!isMobile ? <MiniMap pannable zoomable /> : null}
            {proposalActive && changedNodeIds.length ? (
              <FitChangedNodes nodeIds={changedNodeIds} />
            ) : null}
          </ReactFlow>

          {selected && !canvasLocked ? (
            <div className="absolute bottom-2 left-2 right-2 z-10 max-h-[46%] overflow-auto rounded-lg border bg-white/95 p-3 shadow-lg backdrop-blur sm:bottom-3 sm:left-3 sm:right-auto sm:max-h-[42%] sm:w-[360px]">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-xs font-medium uppercase text-slate-500">Węzeł</span>
                <button type="button" onClick={removeSelected} className="text-red-500">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <Input
                className="mb-2"
                value={selected.title}
                onChange={(e) => patchSelected({ title: e.target.value })}
              />
              <textarea
                className="mb-2 w-full rounded-md border px-2 py-1.5 text-sm"
                rows={3}
                value={selected.body}
                onChange={(e) => patchSelected({ body: e.target.value })}
                placeholder="Notatka / opis"
              />
              <div className="mb-2 grid grid-cols-3 gap-2">
                <select
                  className="rounded border px-1 py-1 text-xs"
                  value={selected.kind}
                  onChange={(e) => patchSelected({ kind: e.target.value as MindmapNodeKind })}
                >
                  {KINDS.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
                <select
                  className="rounded border px-1 py-1 text-xs"
                  value={selected.class}
                  onChange={(e) => patchSelected({ class: e.target.value as MindmapNodeClass })}
                >
                  {CLASSES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <select
                  className="rounded border px-1 py-1 text-xs"
                  value={selected.status}
                  onChange={(e) => patchSelected({ status: e.target.value as MindmapNodeStatus })}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                className="mb-2"
                placeholder="tagi po przecinku"
                value={(selected.tags || []).join(", ")}
                onChange={(e) =>
                  patchSelected({
                    tags: e.target.value
                      .split(",")
                      .map((t) => t.trim().toLowerCase())
                      .filter(Boolean),
                  })
                }
              />
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-xs text-slate-500">Rozmiar / grubość linii</span>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 w-7 p-0"
                    disabled={clampScale(selected.scale) <= NODE_SCALE_MIN}
                    onClick={() => bumpSelectedScale(-1)}
                    title="Zmniejsz (−)"
                  >
                    −
                  </Button>
                  <span className="w-6 text-center text-xs font-medium tabular-nums">
                    {clampScale(selected.scale)}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 w-7 p-0"
                    disabled={clampScale(selected.scale) >= NODE_SCALE_MAX}
                    onClick={() => bumpSelectedScale(1)}
                    title="Zwiększ (+)"
                  >
                    +
                  </Button>
                </div>
              </div>
              <div className="mb-2">
                <div className="mb-1 text-xs text-slate-500">Kolor</div>
                <div className="flex flex-wrap gap-1.5">
                  {STICKY_COLORS.map((c) => {
                    const active = (selected.color || "white") === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        title={c}
                        onClick={() => patchSelected({ color: c })}
                        className={cn(
                          "h-6 w-6 rounded-full",
                          colorSwatch(c),
                          active && "ring-2 ring-indigo-500 ring-offset-1",
                        )}
                      />
                    );
                  })}
                </div>
              </div>
              {selected.kind === "portal" ? (
                <select
                  className="mb-2 w-full rounded border px-2 py-1 text-xs"
                  value={selected.target_map_id || ""}
                  onChange={(e) => patchSelected({ target_map_id: e.target.value })}
                >
                  <option value="">Wybierz mapę docelową</option>
                  {mapsIndex
                    .filter((m) => m.id !== id)
                    .map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.title}
                      </option>
                    ))}
                </select>
              ) : null}
              <label className="mb-2 flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={Boolean(selected.checked)}
                  onChange={(e) =>
                    patchSelected({
                      checked: e.target.checked,
                      status: e.target.checked ? "done" : selected.status,
                    })
                  }
                />
                Odhaczone lokalnie
              </label>
              <div className="mb-2 space-y-1">
                <div className="flex items-center gap-1 text-xs font-medium text-slate-500">
                  <CheckSquare className="h-3.5 w-3.5" /> Checklist
                </div>
                {(selected.checkboxes || []).map((c) => (
                  <label key={c.id} className="flex items-start gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={c.done}
                      onChange={(e) =>
                        patchSelected({
                          checkboxes: (selected.checkboxes || []).map((x) =>
                            x.id === c.id ? { ...x, done: e.target.checked } : x,
                          ),
                        })
                      }
                    />
                    <span>{c.text}</span>
                  </label>
                ))}
                <button
                  type="button"
                  className="text-xs text-indigo-600"
                  onClick={() => {
                    const text = window.prompt("Tekst checkboxa");
                    if (!text?.trim()) return;
                    patchSelected({
                      checkboxes: [
                        ...(selected.checkboxes || []),
                        { id: crypto.randomUUID().slice(0, 8), text: text.trim(), done: false },
                      ],
                    });
                  }}
                >
                  + checkbox
                </button>
              </div>
              <div className="mb-2 space-y-1">
                <div className="text-xs font-medium text-slate-500">Linki</div>
                {(selected.links || []).map((l, i) => (
                  <div
                    key={`${l.type}-${l.ref}-${i}`}
                    className="flex items-center gap-1 rounded border px-1 py-0.5"
                  >
                    <button
                      type="button"
                      className="flex min-w-0 flex-1 items-center gap-1 px-1 py-1 text-left text-xs hover:bg-slate-50"
                      onClick={() => void openLink(l)}
                    >
                      <ExternalLink className="h-3 w-3 shrink-0" />
                      <span className="truncate">
                        [{l.type}] {l.label || l.ref}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="shrink-0 rounded px-1.5 py-1 text-[10px] text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                      title="Usuń link"
                      onClick={() =>
                        patchSelected({
                          links: (selected.links || []).filter((_, idx) => idx !== i),
                        })
                      }
                    >
                      ×
                    </button>
                  </div>
                ))}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <select
                    className="min-w-0 flex-1 rounded border px-2 py-1 text-xs"
                    defaultValue=""
                    onChange={(e) => {
                      const mid = e.target.value;
                      e.target.value = "";
                      if (mid) addMapLink(mid);
                    }}
                  >
                    <option value="">+ link do mapy myśli…</option>
                    {mapsIndex
                      .filter((m) => m.id !== id)
                      .filter(
                        (m) =>
                          !(selected.links || []).some((l) => l.type === "map" && l.ref === m.id),
                      )
                      .map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.title}
                        </option>
                      ))}
                  </select>
                  <button
                    type="button"
                    className="text-xs text-indigo-600"
                    onClick={() => {
                      const type = window.prompt(
                        "Typ linku: route | doc | task | map | wiedza | ssot | external",
                        "route",
                      ) as MindmapLink["type"] | null;
                      if (!type) return;
                      if (type === "map") {
                        const pick = window.prompt(
                          "ID mapy albo wybierz z listy powyżej",
                          mapsIndex.find((m) => m.id !== id)?.id || "",
                        );
                        if (!pick?.trim()) return;
                        addMapLink(pick.trim());
                        return;
                      }
                      const ref = window.prompt("Ref (np. /kanban albo id)", "/kanban");
                      if (!ref?.trim()) return;
                      const label = window.prompt("Etykieta (opcjonalnie)") || undefined;
                      patchSelected({
                        links: [...(selected.links || []), { type, ref: ref.trim(), label }],
                      });
                    }}
                  >
                    + inny link
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!selectedId || promoteM.isPending}
                  title="Tworzy nowe zadanie w Kanbanie z tytułu i opisu tego węzła"
                  onClick={() => promoteM.mutate()}
                >
                  {promoteM.isPending ? (
                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  ) : null}
                  Utwórz zadanie w Kanbanie
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!selectedId || linkDocM.isPending}
                  title="Tworzy nowy dokument w Dokumentach z tytułu i opisu tego węzła"
                  onClick={() => linkDocM.mutate()}
                >
                  {linkDocM.isPending ? (
                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <FileText className="mr-1 h-3.5 w-3.5" />
                  )}
                  Utwórz dokument
                </Button>
                {selected.kind === "portal" && selected.target_map_id ? (
                  <Button
                    size="sm"
                    onClick={() => {
                      pushHistory(id);
                      navigate(`/mapy/${selected.target_map_id}?from=${encodeURIComponent(id)}`);
                    }}
                  >
                    Otwórz portal
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        {chatOpen ? (
          <>
            {isMobile ? (
              <button
                type="button"
                className="fixed inset-0 z-40 bg-black/40"
                aria-label="Zamknij chat"
                onClick={() => setChatOpen(false)}
              />
            ) : null}
            <aside
              className={cn(
                "flex flex-col overflow-hidden bg-white",
                isMobile
                  ? "fixed inset-x-0 bottom-0 z-50 max-h-[85dvh] rounded-t-2xl border shadow-2xl"
                  : "w-[min(680px,42vw)] shrink-0 rounded-xl border",
              )}
            >
              <div className="flex items-center gap-2 border-b px-3 py-2 text-sm font-medium">
                <MessageSquare className="h-4 w-4 text-indigo-500" />
                Chat o mapie
                {selectedId ? (
                  <Badge variant="secondary" className="text-[10px]">
                    focus 1
                  </Badge>
                ) : (
                  <span className="text-[10px] text-slate-400">cała mapa</span>
                )}
                <SaveOperatorInsightButton
                  surface="mapa_mysli"
                  sourceRef={id || ""}
                  messages={messages.map((m) => ({
                    role: m.role,
                    content: m.content,
                  }))}
                  context={`Mapa: ${map?.title || id}`}
                  disabled={!messages.length}
                  className="ml-auto h-7 text-xs"
                />
                <button
                  type="button"
                  className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  onClick={() => setChatOpen(false)}
                  title="Ukryj chat"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3 text-sm">
                {proposal && diff ? (
                  <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50/80 p-2">
                    <div className="mb-1 text-[10px] font-semibold uppercase text-amber-800">
                      Zmiany w podglądzie
                    </div>
                    <ul className="max-h-36 space-y-1 overflow-y-auto">
                      {changedNodeIds.map((nid) => {
                        const status = diff.nodes[nid];
                        const node = nodes.find((n) => n.id === nid);
                        const title =
                          (node?.data as unknown as MindmapNode | undefined)?.title || nid;
                        return (
                          <li key={nid}>
                            <button
                              type="button"
                              className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-[11px] hover:bg-amber-100"
                              onClick={() => {
                                focusChangedNode(nid);
                                if (isMobile) setChatOpen(false);
                              }}
                            >
                              <span
                                className={cn(
                                  "rounded px-1 text-[9px] font-semibold uppercase text-white",
                                  status === "add" && "bg-emerald-600",
                                  status === "update" && "bg-amber-600",
                                  status === "delete" && "bg-red-600",
                                )}
                              >
                                {status}
                              </span>
                              <span className="truncate">{title}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : null}
                {messages.length === 0 ? (
                  <p className="text-xs text-slate-500">
                    Napisz np. „Zbuduj mapę z tego: …” albo „Dodaj węzeł o MOFU i połącz z hubem”.
                    Chat zaproponuje zmiany na canvasie; Ty zatwierdzisz albo odrzucisz.
                  </p>
                ) : (
                  messages.map((m) => (
                    <div
                      key={m.id}
                      className={cn(
                        "rounded-lg px-2.5 py-2 text-xs leading-relaxed",
                        m.role === "user"
                          ? "bg-indigo-50 text-indigo-950"
                          : "bg-slate-50 text-slate-800",
                      )}
                    >
                      <div className="mb-0.5 text-[10px] uppercase text-slate-400">
                        {m.role === "user" ? "Ty" : "Asystent"}
                      </div>
                      <div className="whitespace-pre-wrap">{m.content}</div>
                    </div>
                  ))
                )}
              </div>
              <form
                className="border-t p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
                onSubmit={(e: FormEvent) => {
                  e.preventDefault();
                  const msg = chatInput.trim();
                  if (!msg || chatM.isPending) return;
                  chatM.mutate(msg);
                }}
              >
                <textarea
                  className="mb-2 w-full rounded-md border px-2 py-1.5 text-sm"
                  rows={isMobile ? 2 : 3}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Zbuduj / zmień mapę albo zadaj pytanie…"
                />
                <Button
                  type="submit"
                  className="w-full"
                  disabled={chatM.isPending || !chatInput.trim()}
                >
                  {chatM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Wyślij"}
                </Button>
              </form>
            </aside>
          </>
        ) : null}
      </div>

      {isMobile && !chatOpen ? (
        <button
          type="button"
          className="fixed bottom-5 right-4 z-30 flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-3 text-sm font-medium text-white shadow-lg"
          onClick={() => setChatOpen(true)}
        >
          <MessageSquare className="h-4 w-4" />
          Chat
        </button>
      ) : null}

      {previewTask ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[80vh] w-full max-w-lg overflow-auto rounded-xl bg-white p-4 shadow-xl">
            <div className="mb-3 flex items-start justify-between gap-2">
              <div>
                <h3 className="font-medium">{previewTask.title}</h3>
                <p className="text-xs text-slate-500">
                  {previewTask.owner} · {previewTask.priority} · {previewTask.bucket}
                </p>
              </div>
              <button type="button" onClick={() => setPreviewTask(null)}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <pre className="whitespace-pre-wrap rounded bg-slate-50 p-3 text-sm text-slate-800">
              {previewTask.description || "(brak opisu)"}
            </pre>
            <div className="mt-3 flex gap-2">
              <Button size="sm" onClick={() => navigate("/kanban")}>
                Otwórz Kanban
              </Button>
              <Button size="sm" variant="outline" onClick={() => setPreviewTask(null)}>
                Zamknij
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {previewDoc ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[80vh] w-full max-w-2xl overflow-auto rounded-xl bg-white p-4 shadow-xl">
            <div className="mb-3 flex items-start justify-between gap-2">
              <div>
                <h3 className="font-medium">{previewDoc.title}</h3>
                <p className="text-xs text-slate-500">
                  {(previewDoc.tags || []).join(", ") || "dokument"}
                </p>
              </div>
              <button type="button" onClick={() => setPreviewDoc(null)}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <pre className="whitespace-pre-wrap rounded bg-slate-50 p-3 text-sm text-slate-800">
              {previewDoc.body_md || "(pusto)"}
            </pre>
            {previewDoc.truncated ? (
              <p className="mt-2 text-xs text-slate-500">Podgląd obcięty…</p>
            ) : null}
            <div className="mt-3 flex gap-2">
              <Button size="sm" onClick={() => navigate(`/dokumenty/${previewDoc.id}`)}>
                Otwórz w Dokumentach
              </Button>
              <Button size="sm" variant="outline" onClick={() => setPreviewDoc(null)}>
                Zamknij
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
